

import React from "react";
import "./AudiobookFlipBook.css";
import AnimatedStoryText from "./AnimatedStoryText";
import AnimatedStoryImage from "./AnimatedStoryImage";

const PAPER = "#FFFFFF";
const TEXT_NAVY = "#23314E";
const ACCENT_BLUE = "#3B82F6";
const MUTED = "#8A96A8";

const renderDesktopStoryText = (text, withDropCap) => {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const sentences = trimmed.split(/(?<=[.!?])\s+/).filter(Boolean);
  const first = sentences[0] || trimmed;
  const restSentences = sentences.slice(1);

  return (
    <div className="storybook-text-block">
      {withDropCap ? (
        <p className="storybook-line storybook-line--first">
          <span className="storybook-drop-cap">{first.charAt(0)}</span>
          {first.slice(1)}
        </p>
      ) : (
        <p className="storybook-line storybook-line--first">{first}</p>
      )}
      {restSentences.length > 0 && (
        <>
          <div className="storybook-divider" aria-hidden="true">
            <span className="storybook-divider-line" />
            <span className="storybook-heart">♥</span>
            <span className="storybook-divider-line" />
          </div>
          <p className="storybook-line">{restSentences.join(" ")}</p>
        </>
      )}
    </div>
  );
};


const AudiobookSinglePage = ({
  page,
  pageNumber,
  totalPages,
  side,
  isPlaying,
  onTogglePlayPause,
  hideReadButton = false,
  bookTitle = "",
  animateStoryText = false,
  storyTextAnimationSpeed = 80,
  waitForNarration = false,
  narrationActive = false,
  animateStoryImage = false,
}) => {
  if (!page) return null;

  if (page.type === "cover" || side === "cover") {
    // Full-bleed cover: the art fills the whole book face (covers are generated
    // 1:1, so a portrait book center-crops the sides via object-cover) with the
    // title + creator overlaid on a legibility gradient, like a real book jacket.
    // The explicit width + aspect ratio give the cover its own size — the
    // wrapper (.storybook-pages-wrap--cover) is a flex row whose sheet shrinks
    // to content, and absolutely-positioned children contribute no size, so
    // percentage/auto sizing here collapses to a sliver.
    return (
      // Outer stays overflow-visible so Read to me can sit to the right of the cover
      // without being clipped; art/title stay inside an overflow-hidden face.
      <div
        className="relative"
        style={{
          background: PAPER,
          aspectRatio: "3 / 4",
          // Third term keeps the whole book visible on short viewports:
          // 152px ≈ nav + top gap + bottom breathing room, ×0.75 converts
          // the available height back to width at the 3:4 ratio.
          width: "min(520px, 88vw, calc((100vh - 152px) * 0.75))",
        }}
      >
        <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
          {page.image_url && (
            <img
              src={page.image_url}
              alt="Cover"
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { e.target.src = "/assets/panda-filled.png"; }}
            />
          )}

          {/* Spine: dark crease + highlight ridge on the left edge so the
              full-bleed jacket still reads as a physical hardcover */}
          <div
            className="absolute inset-y-0 left-0 pointer-events-none z-10"
            style={{
              width: 30,
              background:
                "linear-gradient(90deg, rgba(15,25,45,0.42) 0%, rgba(15,25,45,0.2) 30%, rgba(255,255,255,0.14) 52%, rgba(15,25,45,0.12) 72%, rgba(15,25,45,0) 100%)",
            }}
          />

          {/* Bottom legibility gradient behind the title block */}
          <div
            className="absolute inset-x-0 bottom-0 pointer-events-none"
            style={{
              height: "52%",
              background: "linear-gradient(180deg, rgba(15,25,45,0) 0%, rgba(15,25,45,0.42) 45%, rgba(15,25,45,0.78) 100%)",
            }}
          />

          <div className="absolute inset-x-0 bottom-0 px-6 pb-7 text-center">
            {page.text && (
              <h1
                className="text-3xl md:text-4xl font-bold mb-2"
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  color: "#FFFFFF",
                  textShadow: "0 2px 12px rgba(10,20,40,0.55)",
                  lineHeight: 1.12,
                }}
              >
                {page.text}
              </h1>
            )}
            {page.creator && (
              <p className="text-base md:text-lg" style={{ color: "rgba(255,255,255,0.85)", fontFamily: "'Fredoka', sans-serif" }}>
                Created by <span className="font-semibold" style={{ color: "#8FC1FF" }}>{page.creator}</span>
              </p>
            )}
          </div>
        </div>

        {onTogglePlayPause && !hideReadButton && (
          <div
            className="absolute top-4 z-10"
            style={{ left: "100%", marginLeft: 14 }}
          >
            <button
              type="button"
              onClick={onTogglePlayPause}
              className="storybook-read-btn storybook-read-btn--cover"
              style={{ position: "static" }}
              aria-label={isPlaying ? "Pause" : "Read to me"}
            >
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                {isPlaying ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}
              </svg>
              <span>{isPlaying ? "Pause" : "Read to me"}</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    // Transparent: the page-face behind supplies the paper (white + grain);
    // painting a solid fill here would read as an inset box on the page.
    <div className="w-full h-full relative flex flex-col">
      {side === "mobile" && page.type === "voice_intro" ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3 min-h-[60svh]">
          <img
            src="/assets/pixley-panda-nobg.png"
            alt=""
            aria-hidden="true"
            style={{ width: 72, height: 72, objectFit: "contain" }}
          />
          <p className="text-xl font-bold" style={{ color: TEXT_NAVY, fontFamily: "'Fredoka', sans-serif" }}>
            Let's create your story together!
          </p>
          <p className="text-sm leading-relaxed" style={{ color: MUTED, fontFamily: "'Fredoka', sans-serif", maxWidth: 280 }}>
            When you're ready, press the button below and start talking.
          </p>
        </div>
      ) : side === "mobile" ? (
        <>
          <button
            type="button"
            onClick={onTogglePlayPause}
            className="absolute top-2 right-2 text-white px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5 z-20 text-xs"
            style={{
              background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)",
              fontFamily: "'Fredoka', sans-serif",
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.4), 0 3px 0 #073B8E, 0 8px 18px rgba(0,101,248,0.28)",
              textShadow: "0 1px 0 rgba(9,46,125,0.35)",
              fontWeight: 700,
              letterSpacing: "0.01em",
            }}
          >
            {isPlaying ? "Pause" : "Read to me"}
          </button>
          {animateStoryImage ? (
            <div className="flex items-center justify-center h-[42svh] mb-4">
              <div className="w-full h-full max-h-[42svh] rounded-2xl overflow-hidden">
                <AnimatedStoryImage
                  key={`mobile-img-${pageNumber}`}
                  src={page.image_url || null}
                  alt={`Page ${pageNumber}`}
                  objectFit="contain"
                  enableAnimation
                />
              </div>
            </div>
          ) : page.image_url ? (
            <div className="flex items-center justify-center h-[42svh] mb-4">
              <img src={page.image_url} alt={`Page ${pageNumber}`} className="max-h-full max-w-full object-contain" onError={(e) => { e.target.src = "/assets/panda-filled.png"; }} />
            </div>
          ) : null}
          {page.text && (
            <div className="flex-1 overflow-y-auto px-4 pt-2">
              {animateStoryText ? (
                <AnimatedStoryText
                  text={page.text}
                  animationSpeed={storyTextAnimationSpeed}
                  enableAnimation
                  variant="plain"
                  waitForNarration={waitForNarration}
                  narrationActive={narrationActive}
                  style={{ color: TEXT_NAVY, fontFamily: "Georgia, serif", fontSize: 17, lineHeight: 1.7 }}
                />
              ) : (
                <p className="font-serif text-[17px] leading-[1.7]" style={{ color: TEXT_NAVY }}>{page.text}</p>
              )}
            </div>
          )}
          <div className="mt-3 mb-4 text-center text-xs font-medium" style={{ color: ACCENT_BLUE, fontFamily: "'Fredoka', sans-serif" }}>
            Page {pageNumber} of {totalPages}
          </div>
        </>
      ) : side === "left" && page.type === "voice_intro" ? (
        <>
          <div className="storybook-bookmark-ribbon" aria-hidden="true" />
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3">
            <img
              src="/assets/pixley-panda-nobg.png"
              alt=""
              aria-hidden="true"
              style={{ width: 80, height: 80, objectFit: "contain" }}
            />
            <p
              className="text-xl font-bold"
              style={{ color: TEXT_NAVY, fontFamily: "'Fredoka', sans-serif" }}
            >
              Let's create your story together!
            </p>
            <p className="text-sm leading-relaxed" style={{ color: MUTED, fontFamily: "'Fredoka', sans-serif", maxWidth: 280 }}>
              When you're ready, press the button below and start talking.
            </p>
          </div>
        </>
      ) : side === "left" ? (
        <>
          <div className="storybook-bookmark-ribbon" aria-hidden="true" />

          {!hideReadButton && (
            <button type="button" onClick={onTogglePlayPause} className="storybook-read-btn" aria-label={isPlaying ? "Pause" : "Read to me"}>
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                {isPlaying ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}
              </svg>
              <span>{isPlaying ? "Pause" : "Read to me"}</span>
            </button>
          )}

          {page.text && (
            // Typography (1.1rem/1.6 in a 420px column) is sized so the
            // longest generated page (~120 words, reading level 5) fits with
            // room to spare — one consistent size, no scrolling, no clipping
            <div className="flex-1 flex px-2 py-2 overflow-hidden">
              {animateStoryText ? (
                <AnimatedStoryText
                  text={page.text}
                  animationSpeed={storyTextAnimationSpeed}
                  enableAnimation
                  withDropCap={pageNumber === 1}
                  variant="storybook"
                  waitForNarration={waitForNarration}
                  narrationActive={narrationActive}
                />
              ) : (
                renderDesktopStoryText(page.text, pageNumber === 1)
              )}
            </div>
          )}

          <div className="storybook-page-number">Page {pageNumber} of {totalPages}</div>
        </>
      ) : animateStoryImage ? (
        <div className="storybook-image-frame">
          <AnimatedStoryImage
            key={`page-img-${pageNumber}`}
            src={page.image_url || null}
            alt={`Page ${pageNumber}`}
            objectFit="cover"
            enableAnimation
          />
        </div>
      ) : page.image_url ? (
        <div className="storybook-image-frame">
          <img src={page.image_url} alt={`Page ${pageNumber}`} onError={(e) => { e.target.src = "/assets/panda-filled.png"; }} />
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center" aria-hidden="true">
          <svg
            width="72"
            height="72"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ opacity: 0.22 }}
          >
            <path
              d="M32 14v36M32 14c0 0-3.5-3.5-12-3.5S8 14 8 14v36s3.5-3.5 12-3.5S32 50 32 50M32 14c0 0 3.5-3.5 12-3.5S56 14 56 14v36s-3.5-3.5-12-3.5S32 50 32 50"
              stroke="#0B1A33"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </div>
  );
};

export default AudiobookSinglePage;
